import { Button, Col, Divider, Flex, message, Timeline, Tooltip } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AIEmailIcon,
  COCDismissIcon,
  CoCReplyIcon,
  CrossFillIcon,
  GreenTickIcon,
  MailWhiteIcon,
  StarIcon,
} from '../../../assets/icons/icons';
import {
  add_comment,
  attach_file,
  comment_close,
  FileDownload,
} from '../../../assets/images';
import { truncateByLength, useToken } from '../../../common/helper';
import { getFormattedUserTimeZone } from '../../../common/utils/utils';
import {
  CustomButton,
  CustomShipmentText,
  CustomTitle,
} from '../../../components';
import FileUploadModal from '../FileUploadModal';

import { AlertCompletion, ChainOfCustodyItem, ErrorLike } from '@/apis/types';
import { CoCEventType } from '@/common/enum';
import {
  COCAlertRetryCall,
  fetchMentionUsers,
  fetchShipmentTeams,
  legTypes,
} from '../../../apis/shipment';
import { useShipmentPermission } from '../../../hooks/useShipmentPermission';
import useUnauthorize from '../../../hooks/useUnauthorize';
import useUserDateTimeFormat from '../../../hooks/useUserDateTimeFormat';
import Pill from '../AiTranscripts/ui/Pill';
import AlertNestedSmsRow from './AlertNestedSmsRow';
import AlertNestedCoCNoteRow from './AlertNestedCoCNoteRow';
import CloseEntryDetail from './CloseEntryDetail';
import CoCReplies from './CoCReplies';
import { CocEmailBadges, getInboundEmail } from './cocEmailEntry';
import { useEmailReplyT } from '../AiTranscripts/emailComposer/emailReplyStrings';
import {
  formatAssignmentDetail,
  getMergedTimelineItems,
  mergedTimelineItemKey,
  isManualCloseEntry,
  quoteCommentHtml,
} from './cocTimelineMerge';
import './CoC_CT.css';
import ReplyCommentBox, {
  AllCommonTeams,
  CocCommentCreatedMeta,
  ShipmentTeam,
} from './ReplyCommentBox';
import CoCTimelineDot from './CoCTimelineDot';
import TimelineItemCT from './TimelineItemCT';
import { useSpin } from '@/components/SpinContext';
import { useGateValue } from '@statsig/react-bindings';
import { previewableTypes } from '@/common/static_data';
import { getCocCommentsBottomAnchorId } from '../ControlTower/hooks/useScrollToCocComment';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const AssignmentPersonIcon = () => (
  <svg
    aria-label='assignment'
    className='assignment-person-icon coc-ct__icon-block'
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z'
      fill='var(--text-primary)'
    />
  </svg>
);

const AlertEventParticipantsIcon = () => (
  <svg
    aria-hidden
    data-testid='alert-event-participants-icon'
    className='coc-ct__icon-block coc-ct__no-shrink'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M11.1134 8.75342C12.0267 9.37341 12.6667 10.2134 12.6667 11.3334V13.3334H15.3334V11.3334C15.3334 9.88008 12.9534 9.02008 11.1134 8.75342Z'
      fill='#AFAFAF'
    />
    <path
      d='M6.00008 8.00008C7.47284 8.00008 8.66675 6.80617 8.66675 5.33341C8.66675 3.86066 7.47284 2.66675 6.00008 2.66675C4.52732 2.66675 3.33341 3.86066 3.33341 5.33341C3.33341 6.80617 4.52732 8.00008 6.00008 8.00008Z'
      fill='#AFAFAF'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M10.0001 8.00008C11.4734 8.00008 12.6667 6.80675 12.6667 5.33341C12.6667 3.86008 11.4734 2.66675 10.0001 2.66675C9.68675 2.66675 9.39342 2.73341 9.11341 2.82675C9.66675 3.51341 10.0001 4.38675 10.0001 5.33341C10.0001 6.28008 9.66675 7.15342 9.11341 7.84008C9.39342 7.93341 9.68675 8.00008 10.0001 8.00008Z'
      fill='#AFAFAF'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M6.00008 8.66675C4.22008 8.66675 0.666748 9.56008 0.666748 11.3334V13.3334H11.3334V11.3334C11.3334 9.56008 7.78008 8.66675 6.00008 8.66675Z'
      fill='#AFAFAF'
    />
  </svg>
);

interface ShipmentTeamsResponse {
  data: {
    data: {
      data: {
        common_teams: ShipmentTeam[];
      };
    };
  };
}
interface Shipment {
  chain_of_custody: ChainOfCustodyItem[];
  teams: AllCommonTeams[];
  can_view_ai_transcripts?: boolean;
  can_edit_ai_communications?: boolean;
}

export interface MentionRecipient {
  id: string;
  name: string;
  email: string;
  initials: string;
  author_badge_color?: string;
}

interface CoC_CTProps {
  fetchSingleShipmentData: () => void;
  toggleDrawer: (
    action: 'open' | 'close',
    type: 'callTranscripts' | 'emailTranscripts',
    shipmentId: string,
    data?: AlertCompletion
  ) => void;
  shipment: Shipment;
  shipmentId: string;
  onCommentCreated?: (
    shipmentId: string,
    comment: CocCommentCreatedMeta
  ) => void;
  legType?: legTypes;
}

export interface TimeFormatResult {
  relative: string;
  full: string;
}

/**
 * Chain of Custody Timeline component
 *
 * Displays a timeline of custody events for a shipment with commenting,
 * file attachments, and AI alert completion features.
 *
 * @param fetchSingleShipmentData - Function to refresh shipment data
 * @param toggleDrawer - Function to open transcript drawers
 * @param shipment - Shipment data containing chain of custody
 * @param shipmentId - ID of the shipment
 */
const CoC_CT: React.FC<CoC_CTProps> = ({
  fetchSingleShipmentData,
  toggleDrawer,
  shipment,
  shipmentId,
  onCommentCreated,
  legType,
}) => {
  const { showSpin, hideSpin } = useSpin();
  const { token, decodedUserToken } = useToken();
  const isTimeZone = decodedUserToken()?.time_zone_enable;
  const canViewTranscripts = shipment?.can_view_ai_transcripts !== false;
  const canEditAiCommunications = shipment?.can_edit_ai_communications === true;
  const { formatDateTime } = useUserDateTimeFormat();
  const { unauthorize } = useUnauthorize();
  const { t: tEmail } = useEmailReplyT();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [openCommentInput, setOpenCommentInput] = useState(false);
  const [openReplyCommentInput, setOpenReplyCommentInput] = useState<
    number | string | null
  >(null);
  const [_comments, setComments] = useState('');
  const [_commentsInputError, setCommentsInputError] = useState(false);

  const canCreateShipmentForSupport = useShipmentPermission();
  // With the alert_assignment gate on, a person's Resolve / Dismiss shows its
  // tag and quoted comment on one line, like assignment entries do.
  const closeDetailEnabled = useGateValue('alert_assignment');

  const [mentionRecipients, setMentionRecipients] = useState<
    MentionRecipient[]
  >([]);
  useEffect(() => {
    if (!shipmentId) {
      return;
    }
    fetchMentionUsers(token ?? '', shipmentId)
      .then((res) => {
        const additionalOptions = res?.data?.data?.data || [];
        const formattedAdditionalOptions = additionalOptions.map((item) => ({
          id: item?.id,
          name: item?.name,
          email: item?.email || '',
          initials: item?.badge,
          author_badge_color: item?.badge_color,
        }));
        setMentionRecipients(formattedAdditionalOptions);
      })
      .catch((error: unknown) => {
        unauthorize(error as ErrorLike);
      });
  }, [shipmentId, token, unauthorize]);

  const handleSearchMentions = useCallback(
    async (query: string) => {
      const res = await fetchMentionUsers(
        token ?? '',
        shipmentId,
        undefined,
        query
      );
      const items = res?.data?.data?.data || [];
      return items.map((item) => ({
        id: item?.id,
        name: item?.name,
        email: item?.email || '',
        initials: item?.badge,
        author_badge_color: item?.badge_color,
      }));
    },
    [token, shipmentId]
  );

  const [shipmentTeams, setShipmentTeams] = useState<ShipmentTeam[]>([]);
  useEffect(() => {
    if (!shipmentId) {
      return;
    }
    fetchShipmentTeams(token ?? '', shipmentId)
      .then((res: unknown) => {
        const response = res as ShipmentTeamsResponse;
        const teams = response?.data?.data?.data?.common_teams ?? [];
        setShipmentTeams(teams);
      })
      .catch((error: unknown) => {
        unauthorize(error as ErrorLike);
      });
  }, [shipmentId, token, unauthorize]);

  useEffect(() => {
    setOpenCommentInput(false);
  }, [shipmentId]);

  const resetComments = () => {
    setComments('');
    setCommentsInputError(false);
  };

  const onAddCommentClick = () => {
    setOpenCommentInput(!openCommentInput);
    resetComments();
  };

  const downloadFile = async (
    fileUrl: string,
    fileName: string = 'file'
  ): Promise<void> => {
    if (!fileUrl) {
      message.error('File not found!');
      return;
    }
    const extension = fileName?.split('.').pop()?.toLowerCase();
    if (extension && previewableTypes.includes(extension)) {
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Network error while downloading');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
    }
  };

  const [showFull, setShowFull] = useState(false);
  const getFormattedTime = (ts?: string): TimeFormatResult => {
    let userTz: string | undefined;

    if (isTimeZone) {
      const tzString = getFormattedUserTimeZone();
      const match = tzString.match(/ ([A-Za-z_/]+)$/);
      userTz = match ? match[1] : dayjs.tz.guess();
    }

    let targetTimeInTz;
    if (userTz) {
      targetTimeInTz = dayjs.tz(ts, userTz);
    } else {
      targetTimeInTz = dayjs(ts, 'YYYY-MM-DD HH:mm:ss').local();
    }

    const nowInTz = isTimeZone ? dayjs().tz(userTz) : dayjs();

    const diffSeconds = nowInTz.diff(targetTimeInTz, 'second');
    const diffMinutes = Math.floor(Math.abs(diffSeconds) / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    let relativeTimeResult: string;
    if (diffDays >= 1) {
      relativeTimeResult =
        diffSeconds >= 0
          ? `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
          : `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours >= 1) {
      relativeTimeResult =
        diffSeconds >= 0
          ? `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`
          : `in ${diffHours} hr${diffHours > 1 ? 's' : ''}`;
    } else if (diffMinutes >= 1) {
      relativeTimeResult =
        diffSeconds >= 0
          ? `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`
          : `in ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    } else {
      relativeTimeResult = diffSeconds >= 0 ? 'just now' : 'in a few seconds';
    }

    const fullFormattedTime = formatDateTime(targetTimeInTz, {
      includeTime: true,
    });

    return {
      full: fullFormattedTime,
      relative: relativeTimeResult,
    };
  };

  const userColorMap = useMemo(() => {
    const map: Record<number, string> = {};

    shipment?.chain_of_custody?.forEach((custody: ChainOfCustodyItem) => {
      const user = custody?.created_by;
      if (user && !map[user]) {
        map[user] = custody?.author_badge_color || '#ccc';
      }
    });

    return map;
  }, [shipment?.chain_of_custody]);

  // The timeline items pass the drawer's arguments as loose strings and an
  // object; the drawer itself only knows these two actions and two types.
  const openTranscriptDrawer = (
    action: string,
    type: string,
    drawerShipmentId: string,
    data: object
  ): void => {
    if (action !== 'open' && action !== 'close') return;
    if (type !== 'callTranscripts' && type !== 'emailTranscripts') return;
    toggleDrawer(action, type, drawerShipmentId, data as AlertCompletion);
  };

  function getNotificationLabel(type?: string): string {
    switch (type) {
      case 'AI_CALL':
        return 'AI call';
      case 'AI_EMAIL':
        return 'AI email';
      case 'MOBILE':
        return 'SMS';
      case 'EMAIL':
        return 'Email';
      default:
        return type ?? ''; // fallback (optional)
    }
  }
  const [halfHeight, setHalfHeight] = useState<Record<number | string, number>>(
    {}
  );

  const handleSetHalfHeight = useCallback(
    (id: string | number, height: number) => {
      setHalfHeight((prev) => ({ ...prev, [id]: height }));
    },
    []
  );

  const handleToggleReply = (id: string | number | undefined) => {
    setOpenReplyCommentInput((prev) => (prev === id ? null : (id ?? null)));
  };

  const handleRetryCall = (data: AlertCompletion & { id?: string }): void => {
    if (!data) {
      return;
    }
    showSpin();
    const model = {
      reason: '',
    };
    COCAlertRetryCall(token ?? '', model, data?.id ?? '')
      .then((res) => {
        const responseData = res?.data;
        if (responseData?.data) {
          if (responseData.data.is_success) {
            fetchSingleShipmentData();
            message.success(responseData.data.message);
          } else {
            message.error(responseData.data.message);
          }
        }
      })
      .catch((error: ErrorLike) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const isCL = legType === 'CL';

  return (
    <>
      <Flex gap={4} vertical>
        <Flex wrap justify='space-between' align='center' gap={16}>
          <CustomTitle
            text={themeT('chainOfCustody', theme.themeType)}
            fontSize={15}
            fontWeight={700}
            lineHeight='28px'
            color='var(--text-primary)'
            marginBottom='0px'
          />

          {canCreateShipmentForSupport && (
            <Flex wrap>
              <CustomButton
                width='36px'
                height='36px'
                padding={'6px 10px'}
                fontSize='12px'
                fontWeight='600'
                lineHeight='20px'
                borderRadius='8px'
                bgColor={'var(--surface-container)'}
                color='var(--text-primary)'
                boxShadow='none'
                imageWidth='17px'
                onClick={() => isCL && setUploadModalOpen(true)}
                iconPosition='left'
                gap={8}
                className='coc-ct__glyph-btn'
                image={attach_file}
                disabled={!isCL}
                disableBgColor='var(--surface-container)'
              />
              <FileUploadModal
                fetchSingleShipmentData={fetchSingleShipmentData}
                token={token ?? ''}
                shipmentId={shipmentId}
                modalOpen={uploadModalOpen}
                setModalOpen={setUploadModalOpen}
              />
              <CustomButton
                width='36px'
                height='36px'
                padding={openCommentInput ? '0px' : '6px 8px'}
                fontSize='12px'
                fontWeight='600'
                lineHeight='20px'
                borderRadius='8px'
                bgColor='var(--surface-container)'
                color='var(--text-primary)'
                boxShadow='none'
                onClick={() => isCL && onAddCommentClick()}
                iconPosition='left'
                gap={8}
                className='coc-ct__glyph-btn'
                image={openCommentInput ? comment_close : add_comment}
                disabled={!isCL}
                disableBgColor='var(--surface-container)'
              />
            </Flex>
          )}
        </Flex>

        {isCL && openCommentInput && (
          <div>
            <ReplyCommentBox
              // @ts-expect-error - this was mistyped in the original JavaScript. Needs fix.
              custody={'custody'}
              mentionRecipients={mentionRecipients}
              setOpenReplyCommentInput={() => setOpenCommentInput(false)}
              fetchSingleShipmentData={fetchSingleShipmentData}
              shipmentId={shipmentId}
              specificTeams={shipment?.teams}
              myTeams={shipmentTeams}
              isParentComment={true}
              onSearchMentions={handleSearchMentions}
              onCommentCreated={onCommentCreated}
            />
          </div>
        )}
      </Flex>
      <div className='custom-timeline coc-ct__timeline'>
        <Timeline mode='left'>
          {shipment?.chain_of_custody?.map((custody, index) => {
            return (
              <Timeline.Item
                key={custody.id}
                className='custom-timeline-head'
                dot={
                  <CoCTimelineDot
                    custody={custody}
                    halfHeight={halfHeight[custody.id]}
                    userColor={
                      custody.created_by
                        ? userColorMap[custody.created_by]
                        : undefined
                    }
                  />
                }
              >
                {index === 0 && (
                  <div
                    className='hide-tail start-tail'
                    // eslint-disable-next-line react/forbid-dom-props -- position comes from the event row height measured at runtime (halfHeight)
                    style={{
                      height: halfHeight[custody.id] + 16 || 0,
                    }}
                  ></div>
                )}
                {index === shipment?.chain_of_custody?.length - 1 && (
                  <div
                    className='hide-tail last-tail'
                    // eslint-disable-next-line react/forbid-dom-props -- position comes from the event row height measured at runtime (halfHeight)
                    style={{
                      height: `calc(100% - ${halfHeight[custody.id] + 16}px)`,
                    }}
                  />
                )}

                {/* COC Reply icon — CL only; EL CoC rows are synthetic/read-only */}

                {isCL &&
                  canCreateShipmentForSupport &&
                  custody?.type !== CoCEventType.OneTimeNotification &&
                  custody?.type !== CoCEventType.CustomerNotification && (
                    <span
                      onClick={() => setOpenReplyCommentInput(custody?.id)}
                      className='hover-reply-icon'
                    >
                      <CoCReplyIcon />
                    </span>
                  )}

                {/* coc heading and timestamp */}
                <div
                  id={`coc-${custody.id}`}
                  onClick={() => {
                    if (
                      isCL &&
                      custody?.type !== CoCEventType.OneTimeNotification &&
                      custody?.type !== CoCEventType.CustomerNotification
                    ) {
                      handleToggleReply(custody?.id);
                    }
                  }}
                >
                  <TimelineItemCT
                    custody={custody}
                    setShowFull={setShowFull}
                    showFull={showFull}
                    getFormattedTime={getFormattedTime}
                    downloadFile={downloadFile}
                    setHalfHeight={handleSetHalfHeight}
                    getNotificationLabel={getNotificationLabel}
                    // @ts-expect-error - this was mistyped in the original JavaScript. Needs fix.
                    shipment={shipment}
                    shipmentId={shipmentId}
                    toggleDrawer={openTranscriptDrawer}
                    canViewTranscripts={canViewTranscripts}
                    canEditAiCommunications={canEditAiCommunications}
                    setOpenReplyCommentInput={setOpenReplyCommentInput}
                    fetchSingleShipmentData={fetchSingleShipmentData}
                  />
                </div>

                <Flex wrap>
                  {custody?.attachments?.map((item) => (
                    <Flex vertical key={`${item?.file_url}-${item?.file_name}`}>
                      <Button
                        type='default'
                        className='zl-btn'
                        onClick={() =>
                          downloadFile(item?.file_url, item?.file_name)
                        }
                      >
                        <img
                          src={FileDownload}
                          width={16}
                          alt=''
                          className='zl-invert-on-dark'
                        />
                      </Button>

                      <CustomShipmentText
                        text={truncateByLength(item?.file_name, 6)}
                        fontSize='10px'
                        color='var(--text-secondary)'
                        marginBottom='0'
                      />
                    </Flex>
                  ))}
                </Flex>

                {isCL &&
                openReplyCommentInput === custody?.id &&
                custody?.type !== CoCEventType.OneTimeNotification &&
                custody?.type !== CoCEventType.CustomerNotification ? (
                  <div>
                    <ReplyCommentBox
                      custody={custody}
                      mentionRecipients={mentionRecipients}
                      setOpenReplyCommentInput={setOpenReplyCommentInput}
                      fetchSingleShipmentData={fetchSingleShipmentData}
                      specificTeams={shipment?.teams}
                      myTeams={shipmentTeams}
                      lastMessage={
                        custody?.replies?.[custody.replies.length - 1] || null
                      }
                      shipmentId={shipmentId}
                      onSearchMentions={handleSearchMentions}
                      onCommentCreated={onCommentCreated}
                    />
                  </div>
                ) : null}

                {custody?.replies?.length > 0 && (
                  <Divider
                    titlePlacement='start'
                    plain
                    orientationMargin={0.1}
                    style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      width: '90%',
                    }}
                  >
                    {custody?.replies?.length}{' '}
                    {custody?.replies?.length > 1 ? 'replies ' : 'reply '}
                    {(custody?.hidden_replies_count ?? 0) > 0 &&
                      '(some hidden)'}
                  </Divider>
                )}

                {getMergedTimelineItems(custody)?.map?.((item) => {
                  // Per-email entry of the reply-to-emails feature (the
                  // backend sends `email` only for companies that have it).
                  const inboundEmail =
                    item.type === 'completion'
                      ? getInboundEmail(item.data)
                      : undefined;
                  return (
                    <div
                      key={mergedTimelineItemKey(item)}
                      className='coc-ct__merged-item'
                    >
                      {item.type === 'reply' ? (
                        <>
                          <CoCReplies
                            custody={{ ...custody, replies: [item.data] }}
                            downloadFile={downloadFile}
                            getFormattedTime={getFormattedTime}
                            showFull={showFull}
                            setShowFull={setShowFull}
                            setOpenReplyCommentInput={handleToggleReply}
                          />
                        </>
                      ) : item.type === 'automation_sms' ? (
                        <AlertNestedSmsRow
                          data={item.data}
                          getFormattedTime={getFormattedTime}
                          showFull={showFull}
                          setShowFull={setShowFull}
                        />
                      ) : item.type === 'automation_coc_note' ? (
                        <AlertNestedCoCNoteRow
                          data={item.data}
                          getFormattedTime={getFormattedTime}
                          showFull={showFull}
                          setShowFull={setShowFull}
                          downloadFile={downloadFile}
                        />
                      ) : (
                        <>
                          <Flex
                            gap={12}
                            //align={'center'}
                            style={{
                              marginTop: '12px',
                              marginBottom: '12px',
                            }}
                          >
                            <Col
                              span={1}
                              style={{
                                display: 'flex',
                                justifyContent: 'start',
                                // alignItems: 'center',
                                marginRight: '20px',
                              }}
                            >
                              {/* inbound change here icons */}
                              {item?.data ? (
                                <div
                                  className={`one-time-action-type-icon ${item?.data?.ai_type === 'email' && item?.data?.event === 'Inbound email received' ? 'email-white' : ''}`}
                                >
                                  {/* Entries from the reply-to-emails feature: the dot is the
                                      per-user unread dot, not the status. */}
                                  {(inboundEmail
                                    ? inboundEmail.unread
                                    : item?.data?.status_info?.description ==
                                      'Needs attention') && (
                                    <div className='need-attention-dot'></div>
                                  )}

                                  {item?.data?.activity_type ===
                                  'assignment' ? (
                                    <AssignmentPersonIcon />
                                  ) : item?.data?.status_info?.label?.toLowerCase() ===
                                    'in progress' ? (
                                    <AIEmailIcon />
                                  ) : item?.data?.ai_type === 'email' &&
                                    item?.data?.event ===
                                      'Inbound email received' ? (
                                    <MailWhiteIcon />
                                  ) : item?.data?.status === 'Resolved' ? (
                                    <GreenTickIcon />
                                  ) : item?.data?.status === 'Not Resolved' ? (
                                    <CrossFillIcon />
                                  ) : item?.data?.status_info?.description ===
                                    'Handed off' ? (
                                    <CrossFillIcon />
                                  ) : item?.data?.title ===
                                      'Handed-off to escalation contact' &&
                                    item?.data?.status_info?.description ===
                                      'Needs attention' ? (
                                    <CrossFillIcon />
                                  ) : (
                                    <COCDismissIcon />
                                  )}
                                </div>
                              ) : null}
                            </Col>
                            <Flex
                              vertical
                              justify='space-between'
                              style={{ width: '100%', height: 'stretch' }}
                            >
                              {/* Added style here */}
                              <Flex
                                justify='space-between'
                                style={{ height: '100%' }}
                              >
                                <Tooltip title={item?.data?.title}>
                                  <Flex
                                    align='center'
                                    style={{ height: '100%' }}
                                  >
                                    <CustomShipmentText
                                      text={item?.data?.title}
                                      fontWeight={600}
                                      color='var(--text-primary)'
                                      marginBottom={'0px'}
                                      whiteSpace='pre-line'
                                    />
                                  </Flex>
                                </Tooltip>
                                {item?.data?.timestamp && (
                                  <Flex
                                    align='center'
                                    gap={4}
                                    onClick={() => setShowFull((prev) => !prev)}
                                    style={{
                                      cursor: 'pointer',
                                      height: '100%',
                                    }}
                                  >
                                    <AlertEventParticipantsIcon />
                                    <CustomShipmentText
                                      text={
                                        showFull
                                          ? getFormattedTime(
                                              item?.data?.timestamp
                                            )?.full
                                          : getFormattedTime(
                                              item?.data?.timestamp
                                            )?.relative
                                      }
                                      color='var(--text-light)'
                                      marginBottom='0'
                                      style={{ whiteSpace: 'nowrap' }}
                                    />
                                  </Flex>
                                )}
                              </Flex>
                              {item?.data?.activity_type === 'assignment' &&
                                formatAssignmentDetail(item.data) && (
                                  <CustomShipmentText
                                    text={formatAssignmentDetail(item.data)}
                                    color='var(--text-primary)'
                                    marginBottom='0'
                                    style={{ marginTop: 2 }}
                                  />
                                )}
                              {/* inbound change here  in item?.data tag */}

                              <Flex justify='space-between' align='center'>
                                <div className='w-full coc-ct__min-w-0'>
                                  {(item?.data?.is_inbound_call ||
                                    inboundEmail?.needs_reply ||
                                    item?.data?.status_info?.description ===
                                      'Needs attention' ||
                                    item?.data?.status_info?.description ===
                                      'Resolved via AI' ||
                                    (item?.data?.status_info?.description ?? '')
                                      .toLowerCase()
                                      .startsWith('dismissed due to') ||
                                    item?.data?.status_info?.label?.toLowerCase() ===
                                      'in progress' ||
                                    item?.data?.summary ||
                                    item?.data?.comment ||
                                    (closeDetailEnabled &&
                                      isManualCloseEntry(item.data) &&
                                      item.data.tag_label)) && (
                                    <Flex
                                      gap={8}
                                      align='center'
                                      className='w-full flex-center btw'
                                    >
                                      <Flex vertical gap={0}>
                                        {inboundEmail && (
                                          <CocEmailBadges
                                            email={inboundEmail}
                                          />
                                        )}
                                        {!inboundEmail &&
                                          (item?.data?.status_info
                                            ?.description ===
                                            'Needs attention' ||
                                            item?.data?.status_info
                                              ?.description ===
                                              'Resolved via AI' ||
                                            item?.data?.status_info?.label?.toLowerCase() ===
                                              'in progress') && (
                                            <Pill
                                              style={{ width: 'fit-content' }}
                                            >
                                              {item?.data?.status_info
                                                ?.description ===
                                                'Needs attention' ||
                                              item?.data?.status_info
                                                ?.description ===
                                                'Resolved via AI'
                                                ? item?.data?.status_info
                                                    ?.description
                                                : item?.data?.status_info?.label?.toLowerCase() ===
                                                    'in progress'
                                                  ? 'Processing'
                                                  : null}
                                            </Pill>
                                          )}
                                        {(
                                          item?.data?.status_info
                                            ?.description ?? ''
                                        )
                                          .toLowerCase()
                                          .startsWith('dismissed due to') && (
                                          <CustomShipmentText
                                            text={
                                              item?.data?.status_info
                                                ?.description
                                            }
                                            color='var(--text-light)'
                                            marginTop={'4px'}
                                            marginBottom={'0px'}
                                          />
                                        )}

                                        {closeDetailEnabled &&
                                        isManualCloseEntry(item.data) ? (
                                          <CloseEntryDetail
                                            completion={item.data}
                                          />
                                        ) : (
                                          <CustomShipmentText
                                            htmlString={
                                              item?.data?.activity_type ===
                                                'assignment' &&
                                              (item?.data?.summary ||
                                                item?.data?.comment)
                                                ? quoteCommentHtml(
                                                    item?.data?.summary ||
                                                      item?.data?.comment ||
                                                      ''
                                                  )
                                                : item?.data?.summary ||
                                                  item?.data?.comment
                                            }
                                            color='var(--text-light)'
                                            whiteSpace='pre-line'
                                            marginTop={'8px'}
                                            marginBottom={'4px'}
                                          />
                                        )}

                                        {item?.data?.attachments?.length ? (
                                          <Flex gap={8} wrap>
                                            {item.data.attachments.map(
                                              (attachment) => (
                                                <Flex
                                                  vertical
                                                  align='center'
                                                  key={`${attachment.file_url}-${attachment.file_name}`}
                                                >
                                                  <Button
                                                    type='default'
                                                    className='zl-btn'
                                                    aria-label={`Download ${attachment.file_name}`}
                                                    onClick={() =>
                                                      downloadFile(
                                                        attachment.file_url,
                                                        attachment.file_name
                                                      )
                                                    }
                                                  >
                                                    <img
                                                      src={FileDownload}
                                                      width={16}
                                                      alt=''
                                                      className='zl-invert-on-dark'
                                                    />
                                                  </Button>
                                                  <CustomShipmentText
                                                    text={truncateByLength(
                                                      attachment.file_name,
                                                      6
                                                    )}
                                                    fontSize='10px'
                                                    color='var(--text-secondary)'
                                                    marginBottom='0'
                                                  />
                                                </Flex>
                                              )
                                            )}
                                          </Flex>
                                        ) : null}
                                      </Flex>

                                      {(item?.data?.ai_type === 'call' ||
                                        item?.data?.ai_type === 'email') && (
                                        <Flex
                                          align='center'
                                          justify='center'
                                          style={{
                                            width: '24px',
                                            height: '24px',
                                          }}
                                        >
                                          <StarIcon width={24} height={24} />
                                        </Flex>
                                      )}
                                    </Flex>
                                  )}
                                  {/* to prevent duplicate transcript buttons on UI  */}
                                  <Flex gap={7}>
                                    {canViewTranscripts &&
                                      (item?.data?.has_transcript ||
                                        inboundEmail) && (
                                        <>
                                          {item?.data?.ai_type === 'call' ||
                                          item?.data?.ai_type === 'email' ? (
                                            <CustomButton
                                              text={
                                                item?.data?.ai_type === 'call'
                                                  ? `View call transcript`
                                                  : inboundEmail
                                                    ? tEmail('viewTranscript')
                                                    : 'View email exchange'
                                              }
                                              bgColor='var(--light-gray)'
                                              marginTop='6px'
                                              color='black'
                                              fontSize='12px'
                                              fontWeight='600'
                                              borderRadius='28px'
                                              width={
                                                inboundEmail ? '150px' : '128px'
                                              }
                                              boxShadow='none'
                                              height='28px'
                                              onClick={() => {
                                                toggleDrawer(
                                                  'open',
                                                  item?.data?.ai_type === 'call'
                                                    ? 'callTranscripts'
                                                    : 'emailTranscripts',
                                                  shipmentId,
                                                  item?.data
                                                );
                                              }}
                                            />
                                          ) : null}
                                        </>
                                      )}

                                    {canViewTranscripts &&
                                      inboundEmail?.replyable && (
                                        <CustomButton
                                          text={tEmail('reply')}
                                          bgColor='var(--light-gray)'
                                          marginTop='6px'
                                          color='black'
                                          fontSize='12px'
                                          fontWeight='600'
                                          borderRadius='28px'
                                          width='75px'
                                          boxShadow='none'
                                          height='28px'
                                          onClick={() => {
                                            // The panel (opened on this entry's
                                            // thread) expands its composer.
                                            toggleDrawer(
                                              'open',
                                              'emailTranscripts',
                                              shipmentId,
                                              {
                                                ...item.data,
                                                open_composer: true,
                                              }
                                            );
                                          }}
                                        />
                                      )}

                                    {item?.data?.ai_type === 'call' &&
                                    item?.data?.can_retry ? (
                                      <CustomButton
                                        text='Retry now'
                                        bgColor='var(--light-gray)'
                                        marginTop='6px'
                                        color='black'
                                        fontSize='12px'
                                        fontWeight='600'
                                        borderRadius='28px'
                                        width='75px'
                                        boxShadow='none'
                                        height='28px'
                                        onClick={() => {
                                          handleRetryCall(item.data);
                                        }}
                                      />
                                    ) : null}
                                  </Flex>
                                </div>
                              </Flex>
                            </Flex>
                          </Flex>
                        </>
                      )}
                    </div>
                  );
                })}
              </Timeline.Item>
            );
          })}
        </Timeline>
        {shipmentId ? (
          <div id={getCocCommentsBottomAnchorId(shipmentId)} />
        ) : null}
      </div>
    </>
  );
};

export default CoC_CT;
